import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { SectionLabel } from 'api-paths-cas';
import { getRouteStatusSections, normalizeTaskRoute } from '../store/apply/selectors';

export function useTaskNavigation(): { nextRoute: string | null; previousRoute: string | null } {
  const location = useLocation();
  const sections = useSelector(getRouteStatusSections);

  return useMemo(() => {
    // Extract task route from pathname: /application/{uni}/{userId}/{applicationId}/{taskRoute}
    const pathSegments = location.pathname.split('/');
    const taskRouteIndex = pathSegments.findIndex(segment => segment === 'application');
    if (taskRouteIndex === -1 || pathSegments.length < taskRouteIndex + 4) {
      return { nextRoute: null, previousRoute: null };
    }

    // Extract all segments after applicationId as the task route
    // e.g., '/application/test-uni/user-123/app-123/education-history/details' -> '/education-history/details'
    const taskRouteSegments = pathSegments.slice(taskRouteIndex + 4);
    const currentTaskRoute = taskRouteSegments.length > 0 ? `/${taskRouteSegments.join('/')}` : null;

    if (!currentTaskRoute || !sections) {
      return { nextRoute: null, previousRoute: null };
    }

    // Find the Application section
    const applicationSection = sections.find(section => section.label === SectionLabel.Application);
    if (!applicationSection?.taskGroups) {
      return { nextRoute: null, previousRoute: null };
    }

    // Find current task and its position
    let currentTaskIndex = -1;
    let currentGroupIndex = -1;

    for (let groupIdx = 0; groupIdx < applicationSection.taskGroups.length; groupIdx++) {
      const taskGroup = applicationSection.taskGroups[groupIdx];
      for (let taskIdx = 0; taskIdx < taskGroup.tasks.length; taskIdx++) {
        const task = taskGroup.tasks[taskIdx];
        const normalizedRoute = normalizeTaskRoute(task.route);

        // Exact match
        if (normalizedRoute === currentTaskRoute) {
          currentTaskIndex = taskIdx;
          currentGroupIndex = groupIdx;
          break;
        }

        // Suffix match at segment boundary (e.g., '/personal/education-history' matches '/education-history')
        if (normalizedRoute.endsWith(currentTaskRoute)) {
          currentTaskIndex = taskIdx;
          currentGroupIndex = groupIdx;
          break;
        }
      }
      if (currentTaskIndex !== -1) break;
    }

    // If current task not found, return null
    if (currentTaskIndex === -1 || currentGroupIndex === -1) {
      return { nextRoute: null, previousRoute: null };
    }

    const currentGroup = applicationSection.taskGroups[currentGroupIndex];

    // Find next task
    let nextRoute: string | null = null;
    // Look for next task in same group
    for (let i = currentTaskIndex + 1; i < currentGroup.tasks.length; i++) {
      const nextTask = currentGroup.tasks[i];
      nextRoute = `..${normalizeTaskRoute(nextTask.route)}`;
      break;
    }

    // If no next task in same group, look in next groups
    if (!nextRoute) {
      for (let groupIdx = currentGroupIndex + 1; groupIdx < applicationSection.taskGroups.length; groupIdx++) {
        const nextGroup = applicationSection.taskGroups[groupIdx];
        if (nextGroup.tasks.length > 0) {
          const [nextTask] = nextGroup.tasks;
          nextRoute = `..${normalizeTaskRoute(nextTask.route)}`;
          break;
        }
      }
    }

    // Find previous task
    let previousRoute: string | null = null;
    // Look for previous task in same group
    for (let i = currentTaskIndex - 1; i >= 0; i--) {
      const prevTask = currentGroup.tasks[i];
      previousRoute = `..${normalizeTaskRoute(prevTask.route)}`;
      break;
    }

    // If no previous task in same group, look in previous groups
    if (!previousRoute) {
      for (let groupIdx = currentGroupIndex - 1; groupIdx >= 0; groupIdx--) {
        const prevGroup = applicationSection.taskGroups[groupIdx];
        if (prevGroup.tasks.length > 0) {
          const prevTask = prevGroup.tasks[prevGroup.tasks.length - 1];
          previousRoute = `..${normalizeTaskRoute(prevTask.route)}`;
          break;
        }
      }
    }

    return { nextRoute, previousRoute };
  }, [location.pathname, sections]);
}
